// Boot-time URL routing helper. Pure — takes the location.search string and
// returns one of three classifications the host needs to act on. Separated
// from `main.ts` so it can be unit-tested without booting any of the runtime
// surface (canvas, settings, i18n).

import { decodeShareUrl, type DecodeResult } from '../share/decode.js';

export type Route =
  | { readonly kind: 'shared'; readonly decoded: Extract<DecodeResult, { kind: 'shared' }> }
  | { readonly kind: 'shareError'; readonly reason: 'invalid' | 'outdated' }
  | { readonly kind: 'none' };

/**
 * Classify a `location.search` query string into the shape the boot path needs:
 *
 * - `'shared'` — valid `?seed=…&d=…&v=…` link; host should launch the puzzle.
 * - `'shareError'` — share params present but malformed or version-stale; host
 *   should surface the matching `error.shareLink.*` toast then fall through.
 * - `'none'` — no share-related params at all; host falls through to its normal
 *   resume-or-menu boot path.
 */
export function routeFromUrl(search: string): Route {
  const decoded = decodeShareUrl(search);
  if (decoded.kind === 'shared') return { kind: 'shared', decoded };
  if (decoded.kind === 'invalid') return { kind: 'shareError', reason: decoded.reason };
  return { kind: 'none' };
}
